import { useCallback, useEffect, useState } from "react";

/**
 * State that mirrors itself to `localStorage`, so a page refresh keeps the
 * value while it stays local to the current device/browser. Reads lazily on
 * mount and swallows storage errors (private mode, quota) so a failing store
 * never breaks the UI.
 */
export const useLocalStorageState = <T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] => {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore write failures (private mode / quota) — state still works.
    }
  }, [key, value]);

  const setStoredValue = useCallback(
    (next: T | ((prev: T) => T)) => setValue(next),
    []
  );

  return [value, setStoredValue];
};
